import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import paymentProfileService from '@/services/paymentProfile.service';

function pickInitialSelection(methods = [], preferredId = null) {
  if (!Array.isArray(methods) || methods.length === 0) {
    return null;
  }

  if (preferredId && methods.some((method) => method?.id === preferredId)) {
    return preferredId;
  }

  const defaultMethod = methods.find((method) => method?.is_default);
  if (defaultMethod?.id) {
    return defaultMethod.id;
  }

  return methods[0]?.id ?? null;
}

export default function useAuctionPaymentMethods() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);
  const latestStateRef = useRef({
    paymentMethods: [],
    status: 'idle',
    selectedPaymentMethodId: null,
    defaultPaymentMethodId: null,
  });

  useEffect(() => {
    latestStateRef.current = {
      paymentMethods,
      status,
      selectedPaymentMethodId,
      defaultPaymentMethodId,
    };
  }, [paymentMethods, status, selectedPaymentMethodId, defaultPaymentMethodId]);

  const loadPaymentMethods = useCallback(async ({ force = false } = {}) => {
    const snapshot = latestStateRef.current;
    const responsePayload = {
      methods: snapshot.paymentMethods,
      defaultPaymentMethodId: snapshot.defaultPaymentMethodId,
      selectedPaymentMethodId: snapshot.selectedPaymentMethodId,
    };

    if (!force && (snapshot.status === 'loading' || snapshot.status === 'loaded')) {
      return responsePayload;
    }

    setStatus('loading');
    setError(null);
    latestStateRef.current = { ...snapshot, status: 'loading' };

    try {
      const { response, data } = await paymentProfileService.listPaymentMethods();
      if (!response?.ok) {
        const message = data?.error_message || 'Unable to load payment methods.';
        const apiError = new Error(message);
        apiError.response = response;
        apiError.data = data;
        throw apiError;
      }

      const methods = Array.isArray(data?.payment_methods) ? data.payment_methods : [];
      const defaultId = data?.default_payment_method_id ?? null;
      const resolvedSelection =
        snapshot.selectedPaymentMethodId &&
        methods.some((method) => method?.id === snapshot.selectedPaymentMethodId)
          ? snapshot.selectedPaymentMethodId
          : pickInitialSelection(methods, defaultId);

      setPaymentMethods(methods);
      setDefaultPaymentMethodId(defaultId);
      setSelectedPaymentMethodId(resolvedSelection);
      setStatus('loaded');

      latestStateRef.current = {
        paymentMethods: methods,
        defaultPaymentMethodId: defaultId,
        selectedPaymentMethodId: resolvedSelection,
        status: 'loaded',
      };

      return {
        methods,
        defaultPaymentMethodId: defaultId,
        selectedPaymentMethodId: resolvedSelection,
      };
    }
    catch (loadError) {
      setPaymentMethods([]);
      setDefaultPaymentMethodId(null);
      setSelectedPaymentMethodId(null);
      setStatus('error');
      setError(loadError);

      latestStateRef.current = {
        paymentMethods: [],
        defaultPaymentMethodId: null,
        selectedPaymentMethodId: null,
        status: 'error',
      };

      throw loadError;
    }
  }, []);

  const hasPaymentMethods = paymentMethods.length > 0;
  const isLoadingPaymentMethods = status === 'loading';

  const resolveSelection = useCallback((nextId) => {
    if (!nextId || typeof nextId !== 'string') {
      setSelectedPaymentMethodId(null);
      return;
    }
    const normalized = nextId.trim();
    if (!normalized) {
      setSelectedPaymentMethodId(null);
      return;
    }
    if (!paymentMethods.some((method) => method?.id === normalized)) {
      setSelectedPaymentMethodId(null);
      return;
    }
    setSelectedPaymentMethodId(normalized);
  }, [paymentMethods]);

  const paymentMethodsState = useMemo(() => ({
    paymentMethods,
    paymentMethodsStatus: status,
    paymentMethodsError: error,
    selectedPaymentMethodId,
    defaultPaymentMethodId,
    hasPaymentMethods,
    isLoadingPaymentMethods,
  }), [
    paymentMethods,
    status,
    error,
    selectedPaymentMethodId,
    defaultPaymentMethodId,
    hasPaymentMethods,
    isLoadingPaymentMethods,
  ]);

  const refreshPaymentMethods = useCallback(
    () => loadPaymentMethods({ force: true }),
    [loadPaymentMethods],
  );

  return {
    ...paymentMethodsState,
    loadPaymentMethods,
    refreshPaymentMethods,
    setSelectedPaymentMethodId: resolveSelection,
  };
}
